import Dexie from 'dexie';
import type { Table } from 'dexie';
import type {
  PKMObject, Property, Link, Attachment, ObjectTemplate, SavedQuery,
  QueryGroup, QueryCondition, QueryOperator, ObjectType, Block,
  UserCollection, UserCollectionItem
} from '../models/types';
import type {
  PropertyRelationLocal,
  PropertyOption,
  PropertyFormula,
  PropertyRollup,
  PropertyComputedCache
} from '../types/advanced-properties';
import type { BlockReminder } from '../types/reminders';

// Define the database schema
export class PKMDatabase extends Dexie {
  // Tables
  objects!: Table<PKMObject, string>;
  properties!: Table<Property, string>;
  links!: Table<Link, string>;
  attachments!: Table<Attachment, string>;
  templates!: Table<ObjectTemplate, string>;
  queries!: Table<SavedQuery, string>;
  blocks!: Table<Block, string>;
  userCollections!: Table<UserCollection, string>;
  userCollectionItems!: Table<UserCollectionItem, [string, string]>;

  // Advanced property tables (F058)
  propertyRelations!: Table<PropertyRelation, string>;
  propertyOptions!: Table<PropertyOption, string>;
  propertyFormulas!: Table<PropertyFormula, string>;
  propertyRollups!: Table<PropertyRollup, string>;
  propertyComputedCache!: Table<PropertyComputedCache, string>;

  // Block reminders
  reminders!: Table<BlockReminder, string>;

  // Generic key/value cache (e.g. the email inbox cache). Survives reloads and
  // new tabs, unlike sessionStorage, and is async so it never blocks the main
  // thread. Callers own the serialized shape and TTL semantics.
  kvCache!: Table<KVCacheEntry, string>;

  constructor() {
    super('PKMDatabase');

    // Define schema version 1
    this.version(1).stores({
      objects: 'id, name, type, createdAt, updatedAt, deletedAt, userId',
      properties: 'id, objectId, name, type, createdAt',
      links: 'id, fromObjectId, toObjectId, linkType, createdAt',
      attachments: 'id, objectId, type, createdAt',
      templates: 'id, objectType, createdAt, updatedAt',
    });

    // Version 2: Add queries table
    this.version(2).stores({
      objects: 'id, name, type, createdAt, updatedAt, deletedAt, userId',
      properties: 'id, objectId, name, type, createdAt',
      links: 'id, fromObjectId, toObjectId, linkType, createdAt',
      attachments: 'id, objectId, type, createdAt',
      templates: 'id, objectType, createdAt, updatedAt',
      queries: 'id, name, createdAt, updatedAt',
    });

    // Version 3: Add blocks table
    this.version(3).stores({
      objects: 'id, name, type, createdAt, updatedAt, deletedAt, userId',
      properties: 'id, objectId, name, type, createdAt',
      links: 'id, fromObjectId, toObjectId, linkType, createdAt',
      attachments: 'id, objectId, type, createdAt',
      templates: 'id, objectType, createdAt, updatedAt',
      queries: 'id, name, createdAt, updatedAt',
      blocks: 'id, objectId, type, createdAt, updatedAt, zIndex',
    });

    // Version 4: Add linkability to blocks
    this.version(4).stores({
      objects: 'id, name, type, createdAt, updatedAt, deletedAt, userId',
      properties: 'id, objectId, name, type, createdAt',
      links: 'id, fromObjectId, toObjectId, linkType, createdAt',
      attachments: 'id, objectId, type, createdAt',
      templates: 'id, objectType, createdAt, updatedAt',
      queries: 'id, name, createdAt, updatedAt',
      blocks: 'id, objectId, type, createdAt, updatedAt, zIndex, isLinkable, name',
    });

    // Version 5: Add sourceType index and migrate book/file to source
    this.version(5).stores({
      objects: 'id, name, type, sourceType, createdAt, updatedAt, deletedAt, userId',
      properties: 'id, objectId, name, type, createdAt',
      links: 'id, fromObjectId, toObjectId, linkType, createdAt',
      attachments: 'id, objectId, type, createdAt',
      templates: 'id, objectType, createdAt, updatedAt',
      queries: 'id, name, createdAt, updatedAt',
      blocks: 'id, objectId, type, createdAt, updatedAt, zIndex, isLinkable, name',
    }).upgrade(async (tx) => {
      // Migrate 'book' objects to 'source' with sourceType='book'
      await tx.table('objects').where('type').equals('book').modify((obj: any) => {
        obj.type = 'source';
        obj.sourceType = 'book';
      });

      // Migrate 'file' objects to 'source' with appropriate sourceType
      await tx.table('objects').where('type').equals('file').modify((obj: any) => {
        obj.type = 'source';
        // Determine sourceType based on mimeType
        const mimeType = obj.mimeType || '';
        if (mimeType.includes('pdf')) {
          obj.sourceType = 'pdf';
        } else if (mimeType.startsWith('image/') || /png|jpg|jpeg|gif|webp/i.test(mimeType)) {
          obj.sourceType = 'image';
        } else if (mimeType.startsWith('video/') || /mp4|webm|mov/i.test(mimeType)) {
          obj.sourceType = 'video';
        } else if (mimeType.startsWith('audio/') || /mp3|wav|ogg/i.test(mimeType)) {
          obj.sourceType = 'audio';
        } else {
          obj.sourceType = 'document';
        }
      });
    });

    // Version 6: Add user collections tables
    this.version(6).stores({
      objects: 'id, name, type, sourceType, createdAt, updatedAt, deletedAt, userId',
      properties: 'id, objectId, name, type, createdAt',
      links: 'id, fromObjectId, toObjectId, linkType, createdAt',
      attachments: 'id, objectId, type, createdAt',
      templates: 'id, objectType, createdAt, updatedAt',
      queries: 'id, name, createdAt, updatedAt',
      blocks: 'id, objectId, type, createdAt, updatedAt, zIndex, isLinkable, name',
      userCollections: 'id, name, pinned, sortOrder, userId, createdAt, updatedAt',
      userCollectionItems: '[collectionId+objectId], collectionId, objectId, addedAt',
    });

    // Version 7: Add advanced properties tables (F058)
    this.version(7).stores({
      objects: 'id, name, type, sourceType, createdAt, updatedAt, deletedAt, userId',
      properties: 'id, objectId, name, type, createdAt',
      links: 'id, fromObjectId, toObjectId, linkType, createdAt',
      attachments: 'id, objectId, type, createdAt',
      templates: 'id, objectType, createdAt, updatedAt',
      queries: 'id, name, createdAt, updatedAt',
      blocks: 'id, objectId, type, createdAt, updatedAt, zIndex, isLinkable, name',
      userCollections: 'id, name, pinned, sortOrder, userId, createdAt, updatedAt',
      userCollectionItems: '[collectionId+objectId], collectionId, objectId, addedAt',
      // Advanced property tables
      propertyRelations: 'id, propertyId, sourceObjectId, targetObjectId, [propertyId+sourceObjectId], [propertyId+targetObjectId]',
      propertyOptions: 'id, propertyId, value, position, [propertyId+position]',
      propertyFormulas: 'id, propertyId',
      propertyRollups: 'id, propertyId, relationPropertyId',
      propertyComputedCache: 'id, propertyId, objectId, [propertyId+objectId], isStale',
    });

    // Version 8: Add block reminders table
    this.version(8).stores({
      objects: 'id, name, type, sourceType, createdAt, updatedAt, deletedAt, userId',
      properties: 'id, objectId, name, type, createdAt',
      links: 'id, fromObjectId, toObjectId, linkType, createdAt',
      attachments: 'id, objectId, type, createdAt',
      templates: 'id, objectType, createdAt, updatedAt',
      queries: 'id, name, createdAt, updatedAt',
      blocks: 'id, objectId, type, createdAt, updatedAt, zIndex, isLinkable, name',
      userCollections: 'id, name, pinned, sortOrder, userId, createdAt, updatedAt',
      userCollectionItems: '[collectionId+objectId], collectionId, objectId, addedAt',
      propertyRelations: 'id, propertyId, sourceObjectId, targetObjectId, [propertyId+sourceObjectId], [propertyId+targetObjectId]',
      propertyOptions: 'id, propertyId, value, position, [propertyId+position]',
      propertyFormulas: 'id, propertyId',
      propertyRollups: 'id, propertyId, relationPropertyId',
      propertyComputedCache: 'id, propertyId, objectId, [propertyId+objectId], isStale',
      reminders: 'id, blockId, objectId, reminderAt, status',
    });

    // Version 9: Add compound indexes for AutoRelationService queries
    this.version(9).stores({
      objects: 'id, name, type, sourceType, createdAt, updatedAt, deletedAt, userId',
      properties: 'id, objectId, name, type, createdAt, [objectId+name]',
      links: 'id, fromObjectId, toObjectId, linkType, createdAt',
      attachments: 'id, objectId, type, createdAt',
      templates: 'id, objectType, createdAt, updatedAt',
      queries: 'id, name, createdAt, updatedAt',
      blocks: 'id, objectId, type, createdAt, updatedAt, zIndex, isLinkable, name',
      userCollections: 'id, name, pinned, sortOrder, userId, createdAt, updatedAt',
      userCollectionItems: '[collectionId+objectId], collectionId, objectId, addedAt',
      propertyRelations: 'id, propertyId, sourceObjectId, targetObjectId, [propertyId+sourceObjectId], [propertyId+targetObjectId], [propertyId+sourceObjectId+targetObjectId]',
      propertyOptions: 'id, propertyId, value, position, [propertyId+position]',
      propertyFormulas: 'id, propertyId',
      propertyRollups: 'id, propertyId, relationPropertyId',
      propertyComputedCache: 'id, propertyId, objectId, [propertyId+objectId], isStale',
      reminders: 'id, blockId, objectId, reminderAt, status',
    });

    // Version 10: generic key/value cache table (email inbox cache, etc.)
    this.version(10).stores({
      objects: 'id, name, type, sourceType, createdAt, updatedAt, deletedAt, userId',
      properties: 'id, objectId, name, type, createdAt, [objectId+name]',
      links: 'id, fromObjectId, toObjectId, linkType, createdAt',
      attachments: 'id, objectId, type, createdAt',
      templates: 'id, objectType, createdAt, updatedAt',
      queries: 'id, name, createdAt, updatedAt',
      blocks: 'id, objectId, type, createdAt, updatedAt, zIndex, isLinkable, name',
      userCollections: 'id, name, pinned, sortOrder, userId, createdAt, updatedAt',
      userCollectionItems: '[collectionId+objectId], collectionId, objectId, addedAt',
      propertyRelations: 'id, propertyId, sourceObjectId, targetObjectId, [propertyId+sourceObjectId], [propertyId+targetObjectId], [propertyId+sourceObjectId+targetObjectId]',
      propertyOptions: 'id, propertyId, value, position, [propertyId+position]',
      propertyFormulas: 'id, propertyId',
      propertyRollups: 'id, propertyId, relationPropertyId',
      propertyComputedCache: 'id, propertyId, objectId, [propertyId+objectId], isStale',
      reminders: 'id, blockId, objectId, reminderAt, status',
      kvCache: 'key, updatedAt',
    });
  }
}

// A single key/value cache row. `value` holds the caller's JSON-serializable
// payload; `updatedAt` is an epoch-ms stamp so callers can apply their own TTL.
export interface KVCacheEntry {
  key: string;
  value: unknown;
  updatedAt: number;
}

// Read a cache entry's value, or undefined if missing. TTL is the caller's job.
export const getKVCache = async <T = unknown>(key: string): Promise<{ value: T; updatedAt: number } | undefined> => {
  const row = await db.kvCache.get(key);
  if (!row) return undefined;
  return { value: row.value as T, updatedAt: row.updatedAt };
};

// Write (upsert) a cache entry. `updatedAt` must be supplied by the caller so
// this module stays free of Date.now() side effects at the storage layer.
export const setKVCache = async (key: string, value: unknown, updatedAt: number): Promise<void> => {
  await db.kvCache.put({ key, value, updatedAt });
};

// Delete a cache entry (e.g. on OAuth reconnect to force a refetch).
export const deleteKVCache = async (key: string): Promise<void> => {
  await db.kvCache.delete(key);
};

// Create a singleton instance
export const db = new PKMDatabase();

// Helper functions for common operations

// Generate unique IDs
export const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Get current timestamp
export const now = (): string => {
  return new Date().toISOString();
};

// Objects CRUD
// NOTE: This function delegates to dataRepository to ensure proper sync
export const createObject = async (
  name: string,
  type: PKMObject['type'],
  content: string = '',
  color?: string,
  status?: PKMObject['status']
): Promise<PKMObject> => {
  // Import dataRepository dynamically to avoid circular dependency
  const { dataRepository } = await import('../repository/DataRepository');
  return dataRepository.createObject(name, type, content, color, status);
};

export const getObject = async (id: string): Promise<PKMObject | undefined> => {
  return await db.objects.get(id);
};

export const updateObject = async (
  id: string,
  updates: Partial<PKMObject>
): Promise<void> => {
  await db.objects.update(id, {
    ...updates,
    updatedAt: now(),
  });
};

export const deleteObject = async (id: string): Promise<void> => {
  // Soft delete
  await db.objects.update(id, {
    deletedAt: now(),
  });
};

export const getAllObjects = async (): Promise<PKMObject[]> => {
  return await db.objects
    .filter((obj) => !obj.deletedAt)
    .reverse()
    .sortBy('updatedAt');
};

// Get top-level objects (not contained by any other object)
export const getTopLevelObjects = async (): Promise<PKMObject[]> => {
  const allObjects = await getAllObjects();
  const containedIds = new Set(
    (await db.links.where('linkType').equals('contains').toArray())
      .map(link => link.toObjectId)
  );
  return allObjects.filter(obj => !containedIds.has(obj.id));
};

// Get objects contained by a specific object
export const getContainedObjects = async (objectId: string): Promise<PKMObject[]> => {
  const containLinks = await db.links
    .where('fromObjectId')
    .equals(objectId)
    .and(link => link.linkType === 'contains')
    .toArray();

  const containedIds = containLinks.map(link => link.toObjectId);
  const objects = await Promise.all(containedIds.map(id => getObject(id)));
  return objects.filter((obj): obj is PKMObject => obj !== undefined && !obj.deletedAt);
};

// Properties CRUD
export const createProperty = async (
  objectId: string,
  name: string,
  type: Property['type'],
  value: Property['value']
): Promise<Property> => {
  const property: Property = {
    id: generateId(),
    objectId,
    name,
    type,
    value,
    createdAt: now(),
    updatedAt: now(),
  };

  await db.properties.add(property);
  return property;
};

export const getPropertiesForObject = async (
  objectId: string
): Promise<Property[]> => {
  return await db.properties.where('objectId').equals(objectId).toArray();
};

export const updateProperty = async (
  id: string,
  updates: Partial<Property>
): Promise<void> => {
  await db.properties.update(id, {
    ...updates,
    updatedAt: now(),
  });
};

export const deleteProperty = async (id: string): Promise<void> => {
  await db.properties.delete(id);
};

// Get all unique values for a specific property name across objects of a type
export const getPropertySuggestions = async (
  propertyName: string,
  objectType: ObjectType
): Promise<string[]> => {
  // Get all objects of this type
  const objects = await db.objects.where('type').equals(objectType).toArray();
  const objectIds = objects.map(obj => obj.id);

  // Get all properties with this name for those objects
  const properties = await db.properties
    .where('name')
    .equals(propertyName)
    .toArray();

  // Filter to only properties belonging to objects of this type
  const relevantProps = properties.filter(prop => objectIds.includes(prop.objectId));

  // Get unique string values
  const uniqueValues = new Set<string>();
  relevantProps.forEach(prop => {
    if (typeof prop.value === 'string' && prop.value.trim()) {
      uniqueValues.add(prop.value.trim());
    }
  });

  return Array.from(uniqueValues).sort();
};

// Links CRUD
export const createLink = async (
  fromObjectId: string,
  toObjectId: string,
  linkType: Link['linkType'] = 'related'
): Promise<Link> => {
  const link: Link = {
    id: generateId(),
    fromObjectId,
    toObjectId,
    linkType,
    createdAt: now(),
  };

  await db.links.add(link);
  return link;
};

export const getLinksForObject = async (objectId: string): Promise<Link[]> => {
  return await db.links
    .where('fromObjectId')
    .equals(objectId)
    .or('toObjectId')
    .equals(objectId)
    .toArray();
};

export const deleteLink = async (id: string): Promise<void> => {
  await db.links.delete(id);
};

// Create containment link (object A contains object B)
export const addObjectToContainer = async (
  containerId: string,
  objectId: string
): Promise<Link> => {
  return await createLink(containerId, objectId, 'contains');
};

// Remove object from container
export const removeObjectFromContainer = async (
  containerId: string,
  objectId: string
): Promise<void> => {
  const link = await db.links
    .where('fromObjectId')
    .equals(containerId)
    .and(l => l.toObjectId === objectId && l.linkType === 'contains')
    .first();

  if (link) {
    await deleteLink(link.id);
  }
};

// Attachments CRUD
export const createAttachment = async (
  objectId: string,
  name: string,
  type: Attachment['type'],
  data: {
    filePath?: string;
    fileSize?: number;
    mimeType?: string;
    url?: string;
    title?: string;
    description?: string;
    faviconUrl?: string;
  }
): Promise<Attachment> => {
  const attachment: Attachment = {
    id: generateId(),
    objectId,
    name,
    type,
    ...data,
    createdAt: now(),
    updatedAt: now(),
  };

  await db.attachments.add(attachment);
  return attachment;
};

export const getAttachmentsForObject = async (
  objectId: string
): Promise<Attachment[]> => {
  return await db.attachments.where('objectId').equals(objectId).toArray();
};

export const updateAttachment = async (
  id: string,
  updates: Partial<Attachment>
): Promise<void> => {
  await db.attachments.update(id, {
    ...updates,
    updatedAt: now(),
  });
};

export const deleteAttachment = async (id: string): Promise<void> => {
  await db.attachments.delete(id);
};

// Templates CRUD
export const createTemplate = async (
  objectType: ObjectTemplate['objectType'],
  fields: ObjectTemplate['fields']
): Promise<ObjectTemplate> => {
  const template: ObjectTemplate = {
    id: generateId(),
    objectType,
    fields,
    createdAt: now(),
    updatedAt: now(),
  };

  await db.templates.add(template);
  return template;
};

export const getTemplate = async (objectType: ObjectTemplate['objectType']): Promise<ObjectTemplate | undefined> => {
  return await db.templates.where('objectType').equals(objectType).first();
};

export const getAllTemplates = async (): Promise<ObjectTemplate[]> => {
  return await db.templates.toArray();
};

export const updateTemplate = async (
  id: string,
  updates: Partial<ObjectTemplate>
): Promise<void> => {
  await db.templates.update(id, {
    ...updates,
    updatedAt: now(),
  });
};

export const deleteTemplate = async (id: string): Promise<void> => {
  await db.templates.delete(id);
};

// Query System

// Evaluate a single condition against an object
const evaluateCondition = async (
  obj: PKMObject,
  condition: QueryCondition
): Promise<boolean> => {
  let fieldValue: any;

  // Get field value - could be object field or property
  if (condition.field === 'name') {
    fieldValue = obj.name;
  } else if (condition.field === 'type') {
    fieldValue = obj.type;
  } else if (condition.field === 'content') {
    fieldValue = obj.content;
  } else if (condition.field === 'createdAt') {
    fieldValue = obj.createdAt;
  } else if (condition.field === 'updatedAt') {
    fieldValue = obj.updatedAt;
  } else {
    // It's a property field
    const properties = await getPropertiesForObject(obj.id);
    const prop = properties.find(p => p.name === condition.field);
    fieldValue = prop?.value;
  }

  // Apply operator
  switch (condition.operator) {
    case 'equals':
      if (Array.isArray(condition.value) && Array.isArray(fieldValue)) {
        return JSON.stringify(condition.value.sort()) === JSON.stringify(fieldValue.sort());
      }
      return fieldValue === condition.value;

    case 'not_equals':
      if (Array.isArray(condition.value) && Array.isArray(fieldValue)) {
        return JSON.stringify(condition.value.sort()) !== JSON.stringify(fieldValue.sort());
      }
      return fieldValue !== condition.value;

    case 'contains':
      if (typeof fieldValue === 'string' && typeof condition.value === 'string') {
        return fieldValue.toLowerCase().includes(condition.value.toLowerCase());
      }
      if (Array.isArray(fieldValue) && typeof condition.value === 'string') {
        return fieldValue.some(v =>
          String(v).toLowerCase().includes(condition.value.toLowerCase())
        );
      }
      return false;

    case 'not_contains':
      if (typeof fieldValue === 'string' && typeof condition.value === 'string') {
        return !fieldValue.toLowerCase().includes(condition.value.toLowerCase());
      }
      if (Array.isArray(fieldValue) && typeof condition.value === 'string') {
        return !fieldValue.some(v =>
          String(v).toLowerCase().includes(condition.value.toLowerCase())
        );
      }
      return true;

    case 'greater_than':
      if (typeof fieldValue === 'number' && typeof condition.value === 'number') {
        return fieldValue > condition.value;
      }
      if (typeof fieldValue === 'string' && typeof condition.value === 'string') {
        return fieldValue > condition.value;
      }
      return false;

    case 'less_than':
      if (typeof fieldValue === 'number' && typeof condition.value === 'number') {
        return fieldValue < condition.value;
      }
      if (typeof fieldValue === 'string' && typeof condition.value === 'string') {
        return fieldValue < condition.value;
      }
      return false;

    case 'greater_or_equal':
      if (typeof fieldValue === 'number' && typeof condition.value === 'number') {
        return fieldValue >= condition.value;
      }
      if (typeof fieldValue === 'string' && typeof condition.value === 'string') {
        return fieldValue >= condition.value;
      }
      return false;

    case 'less_or_equal':
      if (typeof fieldValue === 'number' && typeof condition.value === 'number') {
        return fieldValue <= condition.value;
      }
      if (typeof fieldValue === 'string' && typeof condition.value === 'string') {
        return fieldValue <= condition.value;
      }
      return false;

    case 'is_empty':
      if (Array.isArray(fieldValue)) {
        return fieldValue.length === 0;
      }
      return !fieldValue || (typeof fieldValue === 'string' && fieldValue.trim() === '');

    case 'is_not_empty':
      if (Array.isArray(fieldValue)) {
        return fieldValue.length > 0;
      }
      return fieldValue && (typeof fieldValue !== 'string' || fieldValue.trim() !== '');

    default:
      return false;
  }
};

// Evaluate a query group (with AND/OR logic and nested groups)
const evaluateGroup = async (
  obj: PKMObject,
  group: QueryGroup
): Promise<boolean> => {
  // Evaluate all conditions in this group
  const conditionResults = await Promise.all(
    group.conditions.map(condition => evaluateCondition(obj, condition))
  );

  // Evaluate nested groups if any
  const nestedGroupResults = group.groups
    ? await Promise.all(group.groups.map(g => evaluateGroup(obj, g)))
    : [];

  // Combine all results
  const allResults = [...conditionResults, ...nestedGroupResults];

  // Apply logic operator
  if (group.operator === 'AND') {
    return allResults.every(result => result === true);
  } else {
    // OR
    return allResults.some(result => result === true);
  }
};

// Execute a query and return matching objects
export const executeQuery = async (queryGroup: QueryGroup): Promise<PKMObject[]> => {
  const allObjects = await getAllObjects();
  const results: PKMObject[] = [];

  for (const obj of allObjects) {
    const matches = await evaluateGroup(obj, queryGroup);
    if (matches) {
      results.push(obj);
    }
  }

  return results;
};

// Saved Queries CRUD
export const createSavedQuery = async (
  name: string,
  rootGroup: QueryGroup,
  description?: string
): Promise<SavedQuery> => {
  const query: SavedQuery = {
    id: generateId(),
    name,
    description,
    rootGroup,
    createdAt: now(),
    updatedAt: now(),
  };

  await db.queries.add(query);
  return query;
};

export const getSavedQuery = async (id: string): Promise<SavedQuery | undefined> => {
  return await db.queries.get(id);
};

export const getAllSavedQueries = async (): Promise<SavedQuery[]> => {
  return await db.queries.reverse().sortBy('updatedAt');
};

export const updateSavedQuery = async (
  id: string,
  updates: Partial<SavedQuery>
): Promise<void> => {
  await db.queries.update(id, {
    ...updates,
    updatedAt: now(),
  });
};

export const deleteSavedQuery = async (id: string): Promise<void> => {
  await db.queries.delete(id);
};

// Blocks CRUD
export const createBlock = async (block: Omit<Block, 'id' | 'createdAt' | 'updatedAt'>): Promise<Block> => {
  const newBlock: Block = {
    ...block,
    id: generateId(),
    createdAt: now(),
    updatedAt: now(),
  };

  await db.blocks.add(newBlock);
  return newBlock;
};

export const getBlock = async (id: string): Promise<Block | undefined> => {
  return await db.blocks.get(id);
};

export const getBlocksForObject = async (objectId: string): Promise<Block[]> => {
  return await db.blocks
    .where('objectId')
    .equals(objectId)
    .sortBy('zIndex');
};

export const updateBlock = async (
  id: string,
  updates: Partial<Block>
): Promise<void> => {
  await db.blocks.update(id, {
    ...updates,
    updatedAt: now(),
  });
};

export const deleteBlock = async (id: string): Promise<void> => {
  await db.blocks.delete(id);
};

export const deleteBlocksForObject = async (objectId: string): Promise<void> => {
  await db.blocks.where('objectId').equals(objectId).delete();
};

// ============================================================================
// User Collections CRUD
// ============================================================================

export const createUserCollection = async (
  name: string,
  icon: string = '📁',
  description?: string,
  color?: string,
  pinned: boolean = false
): Promise<UserCollection> => {
  const collection: UserCollection = {
    id: generateId(),
    name,
    description,
    icon,
    color,
    pinned,
    sortOrder: 0,
    createdAt: now(),
    updatedAt: now(),
  };

  await db.userCollections.add(collection);
  return collection;
};

export const getUserCollection = async (id: string): Promise<UserCollection | undefined> => {
  return await db.userCollections.get(id);
};

export const getAllUserCollections = async (): Promise<UserCollection[]> => {
  return await db.userCollections.toArray();
};

export const getPinnedUserCollections = async (): Promise<UserCollection[]> => {
  return await db.userCollections
    .where('pinned')
    .equals(1) // Dexie stores booleans as 0/1
    .sortBy('sortOrder');
};

export const updateUserCollection = async (
  id: string,
  updates: Partial<UserCollection>
): Promise<void> => {
  await db.userCollections.update(id, {
    ...updates,
    updatedAt: now(),
  });
};

export const deleteUserCollection = async (id: string): Promise<void> => {
  // Delete all items in this collection first
  await db.userCollectionItems.where('collectionId').equals(id).delete();
  // Then delete the collection
  await db.userCollections.delete(id);
};

// Collection Items CRUD

export const addObjectToCollection = async (
  collectionId: string,
  objectId: string,
  note?: string
): Promise<UserCollectionItem> => {
  const item: UserCollectionItem = {
    collectionId,
    objectId,
    addedAt: now(),
    note,
  };

  await db.userCollectionItems.put(item);
  return item;
};

export const removeObjectFromCollection = async (
  collectionId: string,
  objectId: string
): Promise<void> => {
  await db.userCollectionItems.delete([collectionId, objectId]);
};

export const getObjectsInCollection = async (collectionId: string): Promise<string[]> => {
  const items = await db.userCollectionItems
    .where('collectionId')
    .equals(collectionId)
    .toArray();
  return items.map(item => item.objectId);
};

export const getCollectionsForObject = async (objectId: string): Promise<UserCollection[]> => {
  const items = await db.userCollectionItems
    .where('objectId')
    .equals(objectId)
    .toArray();

  const collectionIds = items.map(item => item.collectionId);
  const collections = await Promise.all(
    collectionIds.map(id => getUserCollection(id))
  );

  return collections.filter((c): c is UserCollection => c !== undefined);
};

export const isObjectInCollection = async (
  collectionId: string,
  objectId: string
): Promise<boolean> => {
  const item = await db.userCollectionItems.get([collectionId, objectId]);
  return item !== undefined;
};

export const getCollectionItemCount = async (collectionId: string): Promise<number> => {
  return await db.userCollectionItems
    .where('collectionId')
    .equals(collectionId)
    .count();
};

// ============================================================================
// Database Management
// ============================================================================

/**
 * Clear all data from IndexedDB
 * CRITICAL: Called on logout to prevent data leakage between users
 * IndexedDB is scoped to browser origin, NOT user, so we must clear on logout
 */
export const clearAllData = async (): Promise<void> => {
  console.log('[Database] 🗑️ Clearing all IndexedDB data...');

  try {
    await db.objects.clear();
    await db.properties.clear();
    await db.links.clear();
    await db.attachments.clear();
    await db.templates.clear();
    await db.queries.clear();
    await db.blocks.clear();
    await db.userCollections.clear();
    await db.userCollectionItems.clear();
    await db.propertyRelations.clear();
    await db.propertyOptions.clear();
    await db.propertyFormulas.clear();
    await db.propertyRollups.clear();
    await db.propertyComputedCache.clear();
    await db.reminders.clear();

    // Clear Yjs Y.Doc cache to prevent cross-user state leakage
    const { clearAllYDocs } = await import('../services/BlockYjsService');
    clearAllYDocs();

    console.log('[Database] ✅ All IndexedDB data cleared successfully');
  } catch (error) {
    console.error('[Database] ❌ Error clearing IndexedDB:', error);
    throw error;
  }
};
